import React from 'react';
import {
  Box,
  Container,
  VStack,
  Text,
  Button,
  Icon,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { FiCheckSquare, FiEye, FiBarChart } from 'react-icons/fi';
import PageHeader from './shared/PageHeader';
import { useAuth } from '../contexts/AuthContext';

interface NavigationButtonProps {
  icon: React.ComponentType;
  title: string;
  description: string;
  onClick: () => void;
  colorScheme?: string;
  gradient?: string;
}

const NavigationButton: React.FC<NavigationButtonProps> = ({
  icon,
  title,
  description,
  onClick,
  colorScheme = 'cyan',
  gradient,
}) => {
  const bgGradient = gradient || `linear(135deg, ${colorScheme}.400, ${colorScheme}.600)`;
  const hoverBgGradient = `linear(135deg, ${colorScheme}.500, ${colorScheme}.700)`;
  
  return (
    <Button
      onClick={onClick}
      h="auto"
      minH={{ base: "140px", md: "160px" }}
      p={{ base: 4, md: 6 }}
      bgGradient={bgGradient}
      color="white"
      variant="solid"
      _hover={{
        bgGradient: hoverBgGradient,
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
      }}
      _active={{
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 15px rgba(0,0,0,0.15)',
      }}
      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
      borderRadius="xl"
      whiteSpace="normal"
      w="100%"
      boxShadow="0 4px 20px rgba(0,0,0,0.1)"
      border="1px solid"
      borderColor="whiteAlpha.200"
    >
      <VStack spacing={3} w="full" align="center">
        <Box
          p={3}
          borderRadius="full"
          bg="whiteAlpha.200"
          border="1px solid"
          borderColor="whiteAlpha.300"
        >
          <Icon as={icon} boxSize="32px" color="white" />
        </Box>
        <VStack spacing={1} w="full">
          <Text 
            fontSize={{ base: "lg", md: "xl" }} 
            fontWeight="700"
            textAlign="center"
            lineHeight="shorter"
          >
            {title}
          </Text>
          <Text 
            fontSize={{ base: "sm", md: "md" }} 
            color="whiteAlpha.900"
            textAlign="center"
            lineHeight="short"
            px={2}
            opacity={0.9}
          >
            {description}
          </Text>
        </VStack>
      </VStack>
    </Button>
  );
};

const ContainerVanChecklist: React.FC = () => {
  const navigate = useNavigate();
  const { user, selectedCompany, hasFeature, isSuperAdmin } = useAuth();

  const handlePerformCheck = () => {
    navigate('/container-van-checklist/perform');
  };

  const handleChecklistMonitoring = () => {
    navigate('/container-van-checklist/monitoring');
  };

  const handleChecklistReport = () => {
    navigate('/container-van-checklist/report');
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  if (!user || !selectedCompany) {
    return (
      <Container maxW="md" py={8}>
        <VStack spacing={4}>
          <Text>Please log in and select a company to continue.</Text>
          <Button onClick={() => navigate('/dashboard')} colorScheme="cyan">
            Back to Dashboard
          </Button>
        </VStack>
      </Container>
    );
  }

  return (
    <Box minH="100vh" bgGradient="linear(135deg, blue.50, purple.50, pink.50)" w="100%" overflowX="hidden">
      <PageHeader
        title="Container Van Checklist"
        subtitle="Genset Monitoring & Safety"
        icon="🚛"
        onBack={handleBack}
      />

      {/* Main Content */}
      <Container maxW="6xl" py={12} px={4}>
        <VStack spacing={8} w="100%">

          {/* Action Buttons */}
          <VStack spacing={6} w="100%">
            {(hasFeature("container_van_checklist.perform") || isSuperAdmin) && (
              <NavigationButton
                icon={FiCheckSquare}
                title="PERFORM CHECK"
                description="Start container van genset monitoring and safety inspection"
                onClick={handlePerformCheck}
                colorScheme="cyan"
                gradient="linear(135deg, cyan.400, teal.600)"
              />
            )}
            {(hasFeature("container_van_checklist.monitor") || isSuperAdmin) && (
              <NavigationButton
                icon={FiEye}
                title="CHECKLIST MONITORING"
                description="View and track completed container van inspections"
                onClick={handleChecklistMonitoring}
                colorScheme="teal"
                gradient="linear(135deg, teal.400, green.600)"
              />
            )}

            {(hasFeature("container_van_checklist.analysis.generateAnalytics") || isSuperAdmin) && (
              <NavigationButton
                icon={FiBarChart}
                title="CHECKLIST ANALYTICS"
                description="Generate analytics and trends for container van data"
                onClick={handleChecklistReport}
                colorScheme="purple"
                gradient="linear(135deg, purple.400, pink.600)"
              />
            )}

          </VStack>

        </VStack>
      </Container>
    </Box>
  );
};

export default ContainerVanChecklist;